(function () {
  const EXPAND_SELECTOR = '[data-flex-description-expand]';
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const MOBILE_LIMIT = 250;
  const DESKTOP_LIMIT = 600;
  const MOBILE_MIN_SENTENCES = 2;
  const DURATION_MS = 360;
  const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

  function getPreviewLimit(root) {
    const mobileLimit = parseInt(root.getAttribute('data-flex-description-limit-mobile') || '', 10);
    const desktopLimit = parseInt(root.getAttribute('data-flex-description-limit-desktop') || '', 10);
    if (window.matchMedia('(max-width: 749px)').matches) {
      return Number.isNaN(mobileLimit) ? MOBILE_LIMIT : mobileLimit;
    }
    return Number.isNaN(desktopLimit) ? DESKTOP_LIMIT : desktopLimit;
  }

  function getPreviewMinSentences(root) {
    if (!window.matchMedia('(max-width: 749px)').matches) return 1;

    const minimum = parseInt(root.getAttribute('data-flex-description-min-sentences-mobile') || '', 10);
    return Number.isNaN(minimum) ? MOBILE_MIN_SENTENCES : minimum;
  }

  function buildPreviewText(fullText, limit, minSentences) {
    const text = String(fullText || '').trim();
    const max = Number(limit) || DESKTOP_LIMIT;
    const minimum = Math.max(1, Number(minSentences) || 1);

    if (!text) return '';
    if (text.length <= max) return text;

    if (text.indexOf('.') !== -1) {
      const sentences = text.split('.');
      let buffer = '';
      let sentenceCount = 0;

      for (const part of sentences) {
        const sentence = part.trim();
        if (!sentence) continue;

        const sentenceWithPeriod = `${sentence}.`;
        const candidate = buffer ? `${buffer} ${sentenceWithPeriod}` : sentenceWithPeriod;

        if (candidate.length <= max || sentenceCount < minimum) {
          buffer = candidate;
          sentenceCount += 1;
        } else if (!buffer) {
          return sentenceWithPeriod.length > max ? `${sentenceWithPeriod.slice(0, Math.max(0, max - 3)).trimEnd()}...` : sentenceWithPeriod;
        } else {
          break;
        }
      }

      return buffer || `${text.slice(0, Math.max(0, max - 3)).trimEnd()}...`;
    }

    return `${text.slice(0, Math.max(0, max - 3)).trimEnd()}...`;
  }

  function animateHeight(clip, fromHeight, toHeight) {
    return new Promise((resolve) => {
      if (REDUCED_MOTION || fromHeight === toHeight) {
        clip.style.height = 'auto';
        resolve();
        return;
      }

      clip.style.height = `${fromHeight}px`;
      clip.style.overflow = 'hidden';
      clip.style.transition = `height ${DURATION_MS}ms ${EASING}`;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          clip.style.height = `${toHeight}px`;
        });
      });

      const onEnd = (event) => {
        if (event.target !== clip || event.propertyName !== 'height') return;
        clip.removeEventListener('transitionend', onEnd);
        clip.style.transition = '';
        clip.style.height = 'auto';
        clip.style.overflow = '';
        resolve();
      };

      clip.addEventListener('transitionend', onEnd);
    });
  }

  function setExpandedState({
    root,
    clip,
    collapsedText,
    expandedText,
    moreBtn,
    lessBtn,
    expanded,
  }) {
    root.classList.toggle('is-expanded', expanded);
    collapsedText.hidden = expanded;
    expandedText.hidden = !expanded;
    moreBtn.hidden = expanded;
    lessBtn.hidden = !expanded;
    moreBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    lessBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function bindExpand(root) {
    if (root.dataset.flexDescriptionBound === 'true') return;
    root.dataset.flexDescriptionBound = 'true';

    const clip = root.querySelector('[data-flex-description-clip]');
    const inner = root.querySelector('[data-flex-description-inner]');
    const collapsedText = root.querySelector('[data-flex-description-collapsed]');
    const expandedText = root.querySelector('[data-flex-description-expanded]');
    const moreBtn = root.querySelector('[data-flex-description-more]');
    const lessBtn = root.querySelector('[data-flex-description-less]');

    if (!clip || !inner || !collapsedText || !expandedText || !moreBtn || !lessBtn) return;

    const fullText = (root.getAttribute('data-flex-description-full-text') || expandedText.textContent || collapsedText.textContent || '').trim();
    const previewText = buildPreviewText(fullText, getPreviewLimit(root), getPreviewMinSentences(root));
    const needsExpand = previewText !== fullText;

    if (!needsExpand) {
      collapsedText.textContent = fullText;
      collapsedText.hidden = false;
      expandedText.hidden = true;
      moreBtn.hidden = true;
      lessBtn.hidden = true;
      root.classList.remove('flex-collection-header__description--expandable');
      return;
    }

    collapsedText.textContent = previewText;

    let isExpanded = false;
    let isAnimating = false;

    const expand = async () => {
      if (isAnimating || isExpanded) return;
      isAnimating = true;
      root.classList.add('is-animating');

      const fromHeight = clip.offsetHeight;
      setExpandedState({
        root,
        clip,
        collapsedText,
        expandedText,
        moreBtn,
        lessBtn,
        expanded: true,
      });
      const toHeight = inner.offsetHeight;

      await animateHeight(clip, fromHeight, toHeight);

      isExpanded = true;
      root.classList.remove('is-animating');
      isAnimating = false;
    };

    const collapse = async () => {
      if (isAnimating || !isExpanded) return;
      isAnimating = true;
      root.classList.add('is-animating');

      const fromHeight = clip.offsetHeight;
      setExpandedState({
        root,
        clip,
        collapsedText,
        expandedText,
        moreBtn,
        lessBtn,
        expanded: false,
      });
      const toHeight = inner.offsetHeight;

      await animateHeight(clip, fromHeight, toHeight);

      isExpanded = false;
      root.classList.remove('is-animating');
      isAnimating = false;
    };

    moreBtn.addEventListener('click', expand);
    lessBtn.addEventListener('click', collapse);

    window.addEventListener('resize', () => {
      if (isExpanded || isAnimating) return;
      const nextPreview = buildPreviewText(fullText, getPreviewLimit(root), getPreviewMinSentences(root));
      const nextNeedsExpand = nextPreview !== fullText;

      if (!nextNeedsExpand) {
        collapsedText.textContent = fullText;
        collapsedText.hidden = false;
        expandedText.hidden = true;
        moreBtn.hidden = true;
        lessBtn.hidden = true;
        root.classList.remove('flex-collection-header__description--expandable');
        return;
      }

      root.classList.add('flex-collection-header__description--expandable');
      moreBtn.hidden = false;
      lessBtn.hidden = true;
      if (nextPreview && collapsedText.textContent !== nextPreview) {
        collapsedText.textContent = nextPreview;
      }
    }, { passive: true });

    setExpandedState({
      root,
      clip,
      collapsedText,
      expandedText,
      moreBtn,
      lessBtn,
      expanded: false,
    });
  }

  function init(scope) {
    const context = scope && scope.nodeType === 1 ? scope : document;
    context.querySelectorAll(EXPAND_SELECTOR).forEach(bindExpand);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target) init(event.target);
  });
})();
